/* External */
import {
  memo,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import { ScrollContext, useScrollPixels } from './ScrollContext';
import { START, END, CENTER } from './ScrollPosition';
import { ALWAYS, IF_NEEDED } from './ScrollMode';
import { isFullyVisible } from '../../utils/dom';

/* Styles */
import './ScrollView.scss';

const ScrollView = forwardRef(({
  className,
  style,
  scrollableStyle,
  onScroll,
  children,
  resetScrollPosition,
  propagateEventOnReachTop,
  initialScrollPosition,
  initialMaxOffset,
}, ref) => {
  const [offset, setOffset] = useState(initialScrollPosition);
  const scrollViewRef = useRef();
  const scrollableRef = useRef();
  const scrollData = useRef();

  const getMaxOffset = useCallback(() => {
    let newMaxOffset = (scrollableRef.current.clientHeight - scrollViewRef.current.clientHeight)
      + (scrollableRef.current.offsetTop + scrollViewRef.current.offsetTop);

    if (initialMaxOffset) {
      newMaxOffset = initialMaxOffset;
    } else {
      newMaxOffset = newMaxOffset < 0 ? 0 : newMaxOffset;
    }

    return newMaxOffset;
  }, [initialMaxOffset]);

  const scrollIntoView = useCallback(
    (element, {
      position = START,
      mode = ALWAYS,
    }) => {
      scrollData.current = { element, position, mode };
      if (mode === IF_NEEDED && isFullyVisible(element)) return;

      const elementRect = (
        element.getBoundingClientRect && element.getBoundingClientRect()
      ) || element;
      const scrollableRect = scrollableRef.current.getBoundingClientRect();

      const elementRelativePosition = (elementRect.top - scrollableRect.top)
        + scrollableRef.current.offsetTop;
      const elementHeight = element.clientHeight || elementRect.height;
      const scrollViewHeight = scrollViewRef.current.clientHeight;

      let newOffset;

      switch (position) {
        case END: {
          newOffset = elementRelativePosition - (scrollViewHeight - elementHeight);
          break;
        }
        case CENTER: {
          newOffset = elementRelativePosition - (scrollViewHeight - elementHeight) / 2;
          break;
        }
        // case START: (it is the default)
        default: {
          newOffset = elementRelativePosition;
          break;
        }
      }

      const maxOffset = getMaxOffset();
      if (newOffset < 0) {
        newOffset = 0;
      } else if (newOffset > maxOffset) {
        newOffset = maxOffset;
      }

      setOffset(newOffset);
    },
    [getMaxOffset],
  );

  const parentScrollPixels = useScrollPixels();

  const scrollPixels = useCallback((pixels, childScrollPixels) => {
    setOffset((currentOffset) => {
      const maxOffset = getMaxOffset();
      let newOffset = currentOffset + pixels;
      let remainingDelta = 0;

      if (newOffset < 0) {
        remainingDelta = newOffset;
        newOffset = 0;
        setTimeout(() => {
          parentScrollPixels(remainingDelta);
        }, 0);
      } else if (newOffset > maxOffset) {
        remainingDelta = newOffset - maxOffset;
        newOffset = maxOffset;
        if (childScrollPixels) {
          setTimeout(() => {
            childScrollPixels(remainingDelta);
          }, 0);
        }
      }

      // Manually update style to decrease feedback time for user
      scrollableRef.current.style.transform = `translateY(-${newOffset}px)`;
      return newOffset;
    });
  }, [getMaxOffset, parentScrollPixels]);

  /* eslint-disable-next-line react-hooks/exhaustive-deps -- offset is not always changed */
  useEffect(() => {
    setOffset((current) => {
      if (current > getMaxOffset()) return getMaxOffset();
      if (current < 0) return 0;
      return current;
    });
  });

  const scrollableFullStyle = useMemo(() => ({
    transform: `translateY(-${offset}px)`,
    ...scrollableStyle,
  }), [offset, scrollableStyle]);

  const handleWheel = useCallback((event) => {
    const pixels = event.deltaY;

    if (!propagateEventOnReachTop
      || (propagateEventOnReachTop && offset !== 0 && pixels < 0)
      || pixels > 0) {
      event.stopPropagation();
    }

    if (pixels < 0) {
      scrollPixels(pixels);
    } else {
      parentScrollPixels(pixels, scrollPixels);
    }
  }, [offset, parentScrollPixels, propagateEventOnReachTop, scrollPixels]);

  const scrollToTop = useCallback(() => setOffset(0), []);
  const scrollToBottom = useCallback(() => setOffset(getMaxOffset()), [getMaxOffset]);

  useEffect(() => {
    if (onScroll) {
      onScroll(offset, getMaxOffset());
    }
  }, [getMaxOffset, offset, onScroll]);

  const contextValue = useMemo(() => ({
    scrollPixels,
    scrollIntoView,
    scrollToTop,
    scrollToBottom,
  }), [scrollPixels, scrollIntoView, scrollToTop, scrollToBottom]);

  useEffect(() => {
    if (resetScrollPosition) setOffset(0);
  }, [resetScrollPosition]);

  useImperativeHandle(ref, () => ({
    element: scrollViewRef.current,
    scrollToTop,
    scrollToBottom,
  }), [scrollToBottom, scrollToTop]);

  return (
    <div
      className={`dtv-html5-c-scroll-view ${className}`}
      style={style}
      onWheel={handleWheel}
      ref={scrollViewRef}
    >
      <div
        className="dtv-html5-c-scroll-view__scrollable"
        style={scrollableFullStyle}
        ref={scrollableRef}
      >
        <ScrollContext.Provider value={contextValue}>
          {children}
        </ScrollContext.Provider>
      </div>
    </div>
  );
});

ScrollView.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  scrollableStyle: PropTypes.shape({}),
  onScroll: PropTypes.func,
  children: PropTypes.node,
  resetScrollPosition: PropTypes.bool,
  propagateEventOnReachTop: PropTypes.bool,
  initialScrollPosition: PropTypes.number,
  initialMaxOffset: PropTypes.number,
};

ScrollView.defaultProps = {
  className: '',
  style: undefined,
  scrollableStyle: undefined,
  onScroll: undefined,
  children: undefined,
  resetScrollPosition: false,
  propagateEventOnReachTop: false,
  initialScrollPosition: 0,
  initialMaxOffset: 0,
};

export default memo(ScrollView);
